export const LAYER_ORDER = [
  'hair_tail',
  'hair_back',
  'face',
  'hair_base',
  'eyes',
  'ears',
  'hair_sides',
  'hair_fringes',
  'brows',
];

export const HAIR_LAYERS = new Set(['hair_tail', 'hair_back', 'hair_base', 'hair_sides', 'hair_fringes']);
export const EYE_LAYERS = new Set(['eyes']);

export const PART_VARIANTS = {
  hair_tail: 3,
  hair_back: 5,
  face: 1,
  hair_base: 1,
  eyes: 6,
  ears: 1,
  hair_sides: 6,
  hair_fringes: 6,
  brows: 1,
};

export const FRAME_SIZE = 64;

const HAIR_OUTLINE = [41, 32, 52];
const EYE_IRIS    = [75, 105, 47];
const OUTLINE_L   = rgbToHsl(...HAIR_OUTLINE)[2];

const imageCache = {};

export async function preRenderPortraits(characters) {
  await loadPartImages();
  const canvas = document.createElement('canvas');
  canvas.width = FRAME_SIZE;
  canvas.height = FRAME_SIZE;
  const ctx = canvas.getContext('2d');
  const result = {};
  for (const [id, c] of Object.entries(characters)) {
    if (!c._generated) continue;
    renderPortrait(ctx, c.parts, c.hairColor, c.eyeColor);
    result[id] = canvas.toDataURL('image/png');
  }
  return result;
}

export function loadPartImages() {
  return Promise.all(
    LAYER_ORDER.map(name => new Promise(resolve => {
      if (imageCache[name]) return resolve();
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = `/assets/parts/${name}.png`;
      img.onload = () => { imageCache[name] = img; resolve(); };
      img.onerror = () => resolve();
    }))
  );
}

export function renderPortrait(ctx, parts, hairColor, eyeColor) {
  ctx.clearRect(0, 0, FRAME_SIZE, FRAME_SIZE);
  const hairRgb = hexToRgb(hairColor);
  const eyeRgb  = hexToRgb(eyeColor);

  for (const part of LAYER_ORDER) {
    const idx = parts[part] ?? (LAYER_ORDER.includes(part) ? 0 : -1);
    if (idx === -1) continue;
    const img = imageCache[part];
    if (!img) continue;

    if (HAIR_LAYERS.has(part)) {
      drawTinted(ctx, img, idx, hairRgb, 'hair');
    } else if (EYE_LAYERS.has(part)) {
      drawTinted(ctx, img, idx, eyeRgb, 'eye');
    } else {
      ctx.drawImage(img, idx * FRAME_SIZE, 0, FRAME_SIZE, FRAME_SIZE, 0, 0, FRAME_SIZE, FRAME_SIZE);
    }
  }
}

function hexToRgb(hex) {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return [h, s, l];
}

function hslToRgb(h, s, l) {
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1/3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1/3) * 255),
  ];
}

function drawTinted(ctx, img, frameIdx, tintRgb, mode) {
  const off = document.createElement('canvas');
  off.width = FRAME_SIZE; off.height = FRAME_SIZE;
  const octx = off.getContext('2d');
  octx.drawImage(img, frameIdx * FRAME_SIZE, 0, FRAME_SIZE, FRAME_SIZE, 0, 0, FRAME_SIZE, FRAME_SIZE);

  const imageData = octx.getImageData(0, 0, FRAME_SIZE, FRAME_SIZE);
  const data = imageData.data;
  const [th, ts, tintL] = rgbToHsl(...tintRgb);

  let maxHairL = 0;
  if (mode === 'hair') {
    for (let i = 0; i < data.length; i += 4) {
      if (data[i+3] === 0) continue;
      const r = data[i], g = data[i+1], b = data[i+2];
      if (r === HAIR_OUTLINE[0] && g === HAIR_OUTLINE[1] && b === HAIR_OUTLINE[2]) continue;
      const [,,l] = rgbToHsl(r, g, b);
      if (l > maxHairL) maxHairL = l;
    }
  }

  for (let i = 0; i < data.length; i += 4) {
    if (data[i+3] === 0) continue;
    const r = data[i], g = data[i+1], b = data[i+2];

    if (mode === 'hair') {
      if (r === HAIR_OUTLINE[0] && g === HAIR_OUTLINE[1] && b === HAIR_OUTLINE[2]) continue;
      const [,,l] = rgbToHsl(r, g, b);
      const clampedL = Math.max(OUTLINE_L * 1.1, l);
      const scaledL  = (clampedL / maxHairL) * tintL;
      const finalL   = Math.min(1, Math.max(OUTLINE_L * 1.1, scaledL));
      const finalS   = tintL > 0 ? ts * (scaledL / tintL) : 0;
      const [nr, ng, nb] = hslToRgb(th, finalS, finalL);
      data[i] = nr; data[i+1] = ng; data[i+2] = nb;
    } else if (mode === 'eye') {
      if (!(r === EYE_IRIS[0] && g === EYE_IRIS[1] && b === EYE_IRIS[2])) continue;
      const [eh, es, el] = rgbToHsl(...tintRgb);
      const finalL = Math.max(OUTLINE_L * 1.1, el);
      const [nr, ng, nb] = hslToRgb(eh, es, finalL);
      data[i] = nr; data[i+1] = ng; data[i+2] = nb;
    }
  }

  octx.putImageData(imageData, 0, 0);
  ctx.drawImage(off, 0, 0);
}
